import {
  AutoFixHighRounded,
  ExpandMore,
  GTranslate,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLanguage } from "contexts/language.context";
import { useState } from "react";

export default function TranslatableTextField({
  values,
  onChange,
  noGenerate,
  ...props
}) {
  const { language, languages, translate, label } = useLanguage();
  const [selected, setSelected] = useState(language?.id || "en");
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState({});

  if (typeof values != "object") {
    return <TextField value={values} onChange={onChange} {...props} />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        position: "relative",
        boxSizing: "border-box",
        width: props.fullWidth ? "100%" : undefined,
      }}
    >
      <TextField
        value={values[selected] || ""}
        onChange={(e) => {
          onChange({
            target: { value: { ...values, [selected]: e.target.value } },
          });
        }}
        {...props}
      />
      <Box
        sx={{
          alignSelf: "end",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "end",
          gap: 0.5,
          position: "absolute",
          top: -10,
          mr: 1,
          backgroundColor: "white.main",
          px: 0.5,
        }}
      >
        {languages.map((language) => {
          const variant = selected == language.id ? "contained" : "outlined";
          const color = selected == language.id ? "info" : "default";
          return (
            <Chip
              key={"Language:" + language.id}
              size="small"
              clickable
              label={language.name}
              variant={variant}
              color={color}
              sx={
                selected == language.id
                  ? { color: "white !important" }
                  : { backgroundColor: "white !important" }
              }
              onClick={() => {
                setSelected(language.id);
              }}
            />
          );
        })}
        {noGenerate ? null : (
          <>
            <Tooltip title="Google translate">
              <Chip
                icon={
                  loading?.translate ? (
                    <CircularProgress size={"12px"} />
                  ) : (
                    <GTranslate />
                  )
                }
                disabled={!!loading?.translate}
                label={
                  loading?.translate ? (
                    label("generating")
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      {label("generate")} <ExpandMore />
                    </Box>
                  )
                }
                size="small"
                clickable
                onClick={(e) => {
                  setOpen({
                    ...open,
                    translateMenu: { anchorElement: e.currentTarget },
                  });
                }}
              />
            </Tooltip>
            <Menu
              onClose={() => {
                setOpen({ ...open, translateMenu: false });
              }}
              open={!!open?.translateMenu}
              anchorEl={open?.translateMenu?.anchorElement}
              slotProps={{
                paper: {
                  sx: {
                    width: "200px",
                  },
                },
              }}
            >
              <Typography
                sx={{ fontSize: "1rem", fontWeight: "500", px: 1, pb: 1 }}
              >
                {label("source-language")}
              </Typography>

              {languages
                .filter((language) => language.id != selected)
                .map((language) => (
                  <MenuItem
                    key={"Lang: " + language.id}
                    sx={{ mx: -1 }}
                    onClick={async () => {
                      setLoading({ ...loading, translate: true });
                      setOpen({ ...open, translateMenu: false });

                      const [response, data] = await translate(
                        language.id,
                        selected,
                        values[language.id]
                      );
                      if (response.ok) {
                        onChange({
                          target: { value: { ...values, [selected]: data } },
                        });
                      }

                      setLoading({ ...loading, translate: false });
                    }}
                  >
                    {language.name} - {language.id.toUpperCase()}
                  </MenuItem>
                ))}
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
}
